<template>
  <AppLayout page-title="Invoices" page-subtitle="Bill the company for day-rate field work">
    <div class="pt-6">
      <div class="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 class="text-3xl font-bold text-white">Invoices</h1>
          <p class="text-slate-400 mt-1">Bill the operator: company, contractor, well, day rate, and days worked.</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button class="btn-secondary" type="button" @click="showFromTicket = true">From Field Ticket</button>
          <button class="btn-primary" type="button" @click="openCreate">+ New Invoice</button>
        </div>
      </div>

      <p v-if="error" class="notice error">{{ error }}</p>
      <p v-if="message" class="notice success">{{ message }}</p>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div class="stat">
          <p class="label">Outstanding</p>
          <p class="value">${{ money(outstanding) }}</p>
        </div>
        <div class="stat">
          <p class="label">Paid</p>
          <p class="value">${{ money(paidTotal) }}</p>
        </div>
        <div class="stat">
          <p class="label">Invoices</p>
          <p class="value">{{ invoices.length }}</p>
        </div>
      </div>

      <div class="panel">
        <div v-if="loading" class="empty">Loading invoices…</div>
        <div v-else-if="!invoices.length" class="empty">
          No invoices yet. Create one or convert a field ticket.
        </div>
        <div v-else class="table-wrap">
          <table class="inv-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Bill To</th>
                <th>Contractor</th>
                <th>Well / Location</th>
                <th>Day Rate</th>
                <th>Days</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inv in invoices" :key="inv.id">
                <td>
                  <b>{{ inv.invoiceNumber }}</b>
                  <div class="muted">{{ formatDate(inv.issuedDate) }}</div>
                </td>
                <td>{{ inv.billTo || inv.clientCompany || inv.clientName || '—' }}</td>
                <td>{{ inv.contractorName || issuer.name || '—' }}</td>
                <td>
                  <div>{{ inv.wellName || '—' }}</div>
                  <div v-if="inv.wellLocation || inv.location" class="muted">{{ inv.wellLocation || inv.location }}</div>
                </td>
                <td class="money">${{ money(inv.dayRate) }}</td>
                <td>{{ inv.daysWorked || '—' }}</td>
                <td class="money">${{ money(inv.amount) }}</td>
                <td><span class="pill" :class="'st-' + String(inv.status || '').toLowerCase()">{{ inv.status }}</span></td>
                <td class="actions">
                  <button type="button" class="link" @click="viewInvoice(inv.id)">View</button>
                  <button type="button" class="link" @click="downloadInvoice(inv)">Download PDF</button>
                  <button
                    v-if="inv.status !== 'paid' && inv.status !== 'cancelled'"
                    type="button"
                    class="link danger"
                    @click="voidInvoice(inv)"
                  >Void</button>
                  <button
                    v-if="inv.status !== 'paid'"
                    type="button"
                    class="link danger"
                    @click="deleteInvoice(inv)"
                  >Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showCreate" class="modal-bg no-print" @click.self="showCreate = false">
      <div class="modal">
        <h2>New Invoice</h2>
        <form class="space-y-4" @submit.prevent="createInvoice">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label>Company (Bill To)
              <input v-model="form.billTo" required placeholder="Operator / company name" />
            </label>
            <label>Contractor name
              <input v-model="form.contractorName" required />
            </label>
            <label>Well name
              <input v-model="form.wellName" required placeholder="Well or pad name" />
            </label>
            <label>Location
              <input v-model="form.location" placeholder="Lease, county, or site" />
            </label>
            <label>Day rate
              <input v-model.number="form.dayRate" type="number" min="0" step="0.01" required />
            </label>
            <label>Days worked
              <input v-model.number="form.daysWorked" type="number" min="0" step="0.5" required />
            </label>
            <label>Due date
              <input v-model="form.dueDate" type="date" required />
            </label>
          </div>
          <label>Notes
            <textarea v-model="form.notes" rows="2"></textarea>
          </label>
          <p class="text-right text-white font-semibold">
            Total ${{ money(formTotal) }}
            <span class="muted-inline">{{ form.daysWorked || 0 }} days × ${{ money(form.dayRate) }}</span>
          </p>
          <div class="flex justify-end gap-3">
            <button type="button" class="btn-secondary" @click="showCreate = false">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="saving">{{ saving ? 'Creating…' : 'Create Invoice' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showFromTicket" class="modal-bg no-print" @click.self="showFromTicket = false">
      <div class="modal">
        <h2>Invoice from Field Ticket</h2>
        <p v-if="!tickets.length" class="empty">No tickets found. Create a field ticket first.</p>
        <div v-else class="ticket-pick">
          <button
            v-for="t in tickets"
            :key="t.id"
            type="button"
            class="ticket-row"
            :disabled="!!t.invoiceId || invoicingId === t.id"
            @click="invoiceFromTicket(t)"
          >
            <div>
              <b>{{ t.number }}</b>
              <div class="muted">{{ t.wellName || t.siteName || t.customerName || t.title }} · {{ t.workDate }}</div>
            </div>
            <div class="text-right">
              <div class="money">${{ money(t.totalAmount) }}</div>
              <div class="muted">{{ t.invoiceId ? 'Already invoiced' : 'Convert' }}</div>
            </div>
          </button>
        </div>
        <div class="flex justify-end mt-4">
          <button type="button" class="btn-secondary" @click="showFromTicket = false">Close</button>
        </div>
      </div>
    </div>

    <div v-if="viewing" class="modal-bg invoice-view" @click.self="closeView">
      <div class="view-wrap">
        <div class="view-bar no-print">
          <button type="button" class="btn-secondary" @click="closeView">Close</button>
          <button type="button" class="btn-secondary" @click="downloadInvoice(viewing)">Download PDF</button>
          <button
            v-if="viewing.status !== 'paid'"
            type="button"
            class="btn-secondary danger-btn"
            @click="deleteInvoice(viewing)"
          >Delete</button>
          <button type="button" class="btn-primary" @click="printInvoice">Print</button>
        </div>
        <InvoiceDocument :invoice="viewing" :issuer="issuer" preview />
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import InvoiceDocument from '@/components/invoices/InvoiceDocument.vue'
import { buildApiUrl } from '@/config/api'
import { useUserStore } from '@/stores/userStore'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const loading = ref(true)
const saving = ref(false)
const error = ref('')
const message = ref('')
const invoices = ref<any[]>([])
const tickets = ref<any[]>([])
const viewing = ref<any | null>(null)
const showCreate = ref(false)
const showFromTicket = ref(false)
const invoicingId = ref<number | null>(null)

const form = ref(blankForm())

const issuer = computed(() => ({
  name: userStore.currentUser?.name || 'Contractor',
  company: userStore.currentUser?.company || '',
  email: userStore.currentUser?.email || '',
}))

const outstanding = computed(() =>
  invoices.value
    .filter((i) => !['paid', 'cancelled'].includes(String(i.status)))
    .reduce((sum, i) => sum + Number(i.amount || 0), 0),
)
const paidTotal = computed(() =>
  invoices.value
    .filter((i) => i.status === 'paid')
    .reduce((sum, i) => sum + Number(i.amount || 0), 0),
)
const formTotal = computed(() =>
  Number(form.value.dayRate || 0) * Number(form.value.daysWorked || 0),
)

function blankForm() {
  const due = new Date()
  due.setDate(due.getDate() + 30)
  return {
    billTo: '',
    contractorName: userStore.currentUser?.name || '',
    wellName: '',
    location: '',
    dayRate: 550,
    daysWorked: 1,
    dueDate: due.toISOString().slice(0, 10),
    notes: '',
  }
}

function authHeaders(json = true): Record<string, string> {
  const h: Record<string, string> = { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` }
  if (json) h['Content-Type'] = 'application/json'
  return h
}

function money(value: unknown) {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(value?: string) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function openCreate() {
  form.value = blankForm()
  showCreate.value = true
}

async function loadInvoices() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl('payments/invoices?limit=100'), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load invoices')
    invoices.value = data.invoices || []
  } catch (e: any) {
    error.value = e.message || 'Failed to load invoices'
  } finally {
    loading.value = false
  }
}

async function loadTickets() {
  try {
    const res = await fetch(buildApiUrl('contractor/field-tickets'), { headers: authHeaders() })
    const data = await res.json()
    if (res.ok) tickets.value = data.fieldTickets || []
  } catch {
    tickets.value = []
  }
}

async function viewInvoice(id: number, printAfter = false) {
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`payments/invoices/${id}`), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Invoice not found')
    viewing.value = data.invoice
    if (printAfter) {
      setTimeout(() => window.print(), 150)
    }
  } catch (e: any) {
    error.value = e.message || 'Could not open invoice'
  }
}

function closeView() {
  viewing.value = null
  if (route.query.id || route.query.print) {
    router.replace({ path: '/invoices' })
  }
}

function printInvoice() {
  window.print()
}

async function downloadInvoice(inv: any) {
  if (!inv?.id) return
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`payments/invoices/${inv.id}/download`), { headers: authHeaders(false) })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Download failed')
    }
    const blob = await res.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${inv.invoiceNumber || 'invoice'}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
    message.value = `Downloaded ${inv.invoiceNumber || 'invoice'}`
    setTimeout(() => (message.value = ''), 2000)
  } catch (e: any) {
    error.value = e.message || 'Download failed'
  }
}

async function createInvoice() {
  saving.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl('payments/invoices'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        billTo: form.value.billTo,
        contractorName: form.value.contractorName,
        wellName: form.value.wellName,
        location: form.value.location,
        dayRate: form.value.dayRate,
        daysWorked: form.value.daysWorked,
        dueDate: form.value.dueDate,
        notes: form.value.notes,
        amount: formTotal.value,
      }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Could not create invoice')
    showCreate.value = false
    message.value = `Invoice ${data.invoiceNumber} created`
    await loadInvoices()
    if (data.id) await viewInvoice(data.id)
    setTimeout(() => (message.value = ''), 2500)
  } catch (e: any) {
    error.value = e.message || 'Could not create invoice'
  } finally {
    saving.value = false
  }
}

async function invoiceFromTicket(ticket: any) {
  invoicingId.value = ticket.id
  error.value = ''
  try {
    const res = await fetch(buildApiUrl('payments/invoices/from-ticket'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ ticket_id: ticket.id }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Could not invoice ticket')
    showFromTicket.value = false
    message.value = `Invoice ${data.invoiceNumber} created from ${ticket.number}`
    await Promise.all([loadInvoices(), loadTickets()])
    if (data.id) await viewInvoice(data.id)
    setTimeout(() => (message.value = ''), 2500)
  } catch (e: any) {
    error.value = e.message || 'Could not invoice ticket'
  } finally {
    invoicingId.value = null
  }
}

async function voidInvoice(inv: any) {
  if (!window.confirm(`Void invoice ${inv.invoiceNumber}?`)) return
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`payments/invoices/${inv.id}/void`), {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Void failed')
    await loadInvoices()
    if (viewing.value?.id === inv.id) viewing.value = null
  } catch (e: any) {
    error.value = e.message || 'Void failed'
  }
}

async function deleteInvoice(inv: any) {
  if (!inv?.id) return
  if (!window.confirm(`Delete invoice ${inv.invoiceNumber}? This cannot be undone.`)) return
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`payments/invoices/${inv.id}/delete`), {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Delete failed')
    if (viewing.value?.id === inv.id) viewing.value = null
    message.value = `Invoice ${inv.invoiceNumber} deleted`
    await Promise.all([loadInvoices(), loadTickets()])
    setTimeout(() => (message.value = ''), 2500)
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  }
}

onMounted(async () => {
  await Promise.all([loadInvoices(), loadTickets()])
  if (route.query.new) openCreate()
  if (route.query.id) await viewInvoice(Number(route.query.id), route.query.print === '1')
})
</script>

<style scoped>
.btn-primary { @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg; }
.btn-secondary { @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-200 font-medium rounded-lg; }
.danger-btn { @apply text-rose-300 hover:bg-rose-500/20; }
.notice { @apply mb-4 px-4 py-3 rounded-lg; }
.notice.error { @apply bg-red-500/10 text-red-300 border border-red-500/30; }
.notice.success { @apply bg-emerald-500/10 text-emerald-300 border border-emerald-500/30; }
.stat { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5; }
.stat .label { @apply text-sm text-slate-400; }
.stat .value { @apply text-2xl font-bold text-white mt-1; }
.panel { @apply bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden; }
.empty { @apply text-slate-400 p-8 text-center; }
.table-wrap { overflow-x: auto; }
.inv-table { width: 100%; border-collapse: collapse; min-width: 960px; }
.inv-table th, .inv-table td { padding: 12px 16px; text-align: left; border-bottom: 1px solid rgba(51, 65, 85, 0.5); }
.inv-table th { @apply text-xs uppercase tracking-wider text-slate-400; }
.inv-table td { @apply text-sm text-slate-200; }
.muted { @apply text-xs text-slate-500 mt-0.5; }
.muted-inline { @apply text-xs text-slate-500 font-normal ml-2; }
.money { @apply text-emerald-400 font-semibold; }
.actions { display: flex; gap: 10px; flex-wrap: wrap; }
.link { background: none; border: 0; color: #34d399; font-weight: 600; cursor: pointer; padding: 0; }
.link.danger { color: #fb7185; }
.pill { @apply px-2 py-1 rounded-full text-xs capitalize; }
.st-pending, .st-sent { @apply bg-amber-500/20 text-amber-300; }
.st-paid { @apply bg-emerald-500/20 text-emerald-300; }
.st-cancelled, .st-overdue { @apply bg-red-500/20 text-red-300; }
.modal-bg { @apply fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4; }
.modal { @apply bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto; }
.modal h2 { @apply text-xl font-semibold text-white mb-4; }
.modal h3 { @apply text-sm font-semibold text-slate-200; }
.modal label { @apply block text-sm text-slate-300; }
.modal input, .modal textarea {
  @apply mt-1 w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2;
}
.ticket-pick { display: flex; flex-direction: column; gap: 8px; }
.ticket-row {
  @apply w-full text-left flex items-center justify-between p-4 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 text-white;
}
.ticket-row:disabled { opacity: 0.5; cursor: not-allowed; }
.view-wrap { width: min(860px, 100%); }
.view-bar { display: flex; justify-content: flex-end; gap: 8px; margin-bottom: 12px; }

@media print {
  :global(body *) { visibility: hidden; }
  .invoice-view, .invoice-view * { visibility: visible; }
  .invoice-view {
    position: static;
    background: #fff;
    display: block;
    padding: 0;
  }
  .view-wrap { width: 100%; box-shadow: none; }
}
</style>
